import type { ApprovedProductionCalendar } from '@/shared/production-calendar'

export type CalendarMode = 'weekends_only' | 'ru_federal_five_day'

export interface BusinessDaysResult {
  kind: 'success'
  calendarDays: number
  workingDays: number
  ordinaryWeekendDays: number
  federalHolidayOrTransferDays: number
  swapped: boolean
  rangeStart: string
  rangeEnd: string
  coveredCalendars: readonly ApprovedProductionCalendar[]
}

export interface BusinessDaysCoverageError {
  kind: 'coverage-error'
  missingYears: readonly number[]
  swapped: boolean
  rangeStart: string
  rangeEnd: string
}

export type BusinessDaysCalculation = BusinessDaysResult | BusinessDaysCoverageError

export function calculateBusinessDays(
  start: string,
  end: string,
  includeEndDate: boolean,
  mode: CalendarMode,
  calendars: readonly ApprovedProductionCalendar[],
): BusinessDaysCalculation {
  const swapped = start > end
  const rangeStart = swapped ? end : start
  const rangeEnd = swapped ? start : end
  const years = rangeYears(rangeStart, rangeEnd)
  const calendarsByYear = new Map<number, ApprovedProductionCalendar>(
    calendars.map((calendar) => [calendar.year, calendar]),
  )

  if (mode === 'ru_federal_five_day') {
    const missingYears = years.filter((year) => !calendarsByYear.has(year))
    if (missingYears.length > 0) {
      return { kind: 'coverage-error', missingYears, swapped, rangeStart, rangeEnd }
    }
  }

  const countedEnd = includeEndDate ? rangeEnd : shiftIsoDate(rangeEnd, -1)
  let calendarDays = 0
  let workingDays = 0
  let ordinaryWeekendDays = 0
  let federalHolidayOrTransferDays = 0

  for (let date = rangeStart; date <= countedEnd;) {
    calendarDays += 1
    if (mode === 'weekends_only') {
      if (isIsoWeekend(date)) ordinaryWeekendDays += 1
      else workingDays += 1
    } else {
      const calendar = calendarsByYear.get(Number(date.slice(0, 4)))
      const day = calendar?.days.find((candidate) => candidate.date === date)
      if (!day) throw new Error(`Approved production calendar is incomplete for ${date}`)

      if (day.status === 'working') workingDays += 1
      else if (day.sourceRefs.length === 0) ordinaryWeekendDays += 1
      else federalHolidayOrTransferDays += 1
    }

    if (date === countedEnd) break
    date = shiftIsoDate(date, 1)
  }

  return {
    kind: 'success',
    calendarDays,
    workingDays,
    ordinaryWeekendDays,
    federalHolidayOrTransferDays,
    swapped,
    rangeStart,
    rangeEnd,
    coveredCalendars: mode === 'ru_federal_five_day'
      ? years.map((year) => calendarsByYear.get(year) as ApprovedProductionCalendar)
      : [],
  }
}

function rangeYears(start: string, end: string): number[] {
  const startYear = Number(start.slice(0, 4))
  const endYear = Number(end.slice(0, 4))
  return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index)
}

function shiftIsoDate(value: string, amount: number): string {
  const date = new Date(`${value}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + amount)
  return date.toISOString().slice(0, 10)
}

function isIsoWeekend(value: string): boolean {
  const weekday = new Date(`${value}T00:00:00Z`).getUTCDay()
  return weekday === 0 || weekday === 6
}
